import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage, brochureUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import PhotoUploader from "@/components/PhotoUploader";
import LocationPicker from "@/components/LocationPicker";
import { toast } from "sonner";
import { Loader2, Plus, X, FileText, Upload, ShieldCheck } from "lucide-react";

const empty = {
  name: "",
  developer: "",
  description: "",
  price_min: "",
  price_max: "",
  currency: "USD",
  delivery_month: 12,
  delivery_year: new Date().getFullYear() + 1,
  address: "",
  latitude: null,
  longitude: null,
  payment_plan: [{ label: "Separación", percent: 10 }],
  units: [{ bedrooms: 1, price_from: "", area_min: "", description: "" }],
  photos: [],
  brochure_path: null,
  confotur: false,
};

export default function ProjectForm() {
  const { t } = useLocale();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const brochureInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/projects/${id}`)
      .then(({ data }) => setForm({
        ...empty,
        ...data,
        payment_plan: data.payment_plan?.length ? data.payment_plan : empty.payment_plan,
        units: data.units?.length ? data.units : empty.units,
      }))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const patchPayment = (i, field, value) => {
    const next = [...form.payment_plan];
    next[i] = { ...next[i], [field]: field === "percent" ? Number(value) : value };
    setForm({ ...form, payment_plan: next });
  };
  const addPayment = () =>
    setForm({ ...form, payment_plan: [...form.payment_plan, { label: "", percent: 0 }] });
  const removePayment = (i) =>
    setForm({ ...form, payment_plan: form.payment_plan.filter((_, idx) => idx !== i) });

  const patchUnit = (i, field, value) => {
    const next = [...form.units];
    next[i] = { ...next[i], [field]: value };
    setForm({ ...form, units: next });
  };
  const addUnit = () =>
    setForm({ ...form, units: [...form.units, { bedrooms: 1, price_from: "", area_min: "", description: "" }] });
  const removeUnit = (i) =>
    setForm({ ...form, units: form.units.filter((_, idx) => idx !== i) });

  const uploadBrochure = async (file) => {
    if (!file) return;
    setUploadingBrochure(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/projects/brochure/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, brochure_path: data.path }));
      toast.success("Brochure subido");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setUploadingBrochure(false);
      if (brochureInputRef.current) brochureInputRef.current.value = "";
    }
  };

  const totalPercent = form.payment_plan.reduce((s, x) => s + Number(x.percent || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_min: Number(form.price_min),
        price_max: Number(form.price_max),
        delivery_month: Number(form.delivery_month),
        delivery_year: Number(form.delivery_year),
        latitude: form.latitude != null ? Number(form.latitude) : null,
        longitude: form.longitude != null ? Number(form.longitude) : null,
        payment_plan: form.payment_plan
          .filter((s) => s.label && Number(s.percent) > 0)
          .map((s) => ({ label: s.label, percent: Number(s.percent) })),
        units: form.units
          .filter((u) => u.bedrooms != null)
          .map((u) => ({
            bedrooms: Number(u.bedrooms),
            price_from: u.price_from ? Number(u.price_from) : null,
            area_min: u.area_min ? Number(u.area_min) : null,
            description: u.description || "",
          })),
      };
      if (isEdit) {
        await api.put(`/projects/${id}`, payload);
        toast.success("Proyecto actualizado");
      } else {
        await api.post("/projects", payload);
        toast.success("Proyecto publicado");
      }
      navigate("/dashboard?tab=projects");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-16 text-center text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 md:px-10 pt-8 md:pt-16 pb-16">
      <div className="mb-8">
        <div className="label-eyebrow">{t("nav.projects")}</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl">
          {isEdit ? t("proj_form.edit_title") : t("proj_form.new_title")}
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="project-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_name")}</label>
            <Input required value={form.name} onChange={set("name")} data-testid="pf-name" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_developer")}</label>
            <Input required value={form.developer} onChange={set("developer")} data-testid="pf-developer" />
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("proj_form.f_description")}</label>
          <Textarea rows={4} value={form.description} onChange={set("description")} data-testid="pf-description" />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_price_min")}</label>
            <Input required type="number" min="0" value={form.price_min} onChange={set("price_min")} data-testid="pf-price-min" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_price_max")}</label>
            <Input required type="number" min="0" value={form.price_max} onChange={set("price_max")} data-testid="pf-price-max" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_currency")}</label>
            <Select value={form.currency} onValueChange={(v) => set("currency")(v)}>
              <SelectTrigger data-testid="pf-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD $</SelectItem>
                <SelectItem value="DOP">DOP RD$</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_delivery_month")}</label>
            <Select value={String(form.delivery_month)} onValueChange={(v) => set("delivery_month")(Number(v))}>
              <SelectTrigger data-testid="pf-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {t("projects.months").map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("proj_form.f_delivery_year")}</label>
            <Input required type="number" min="2024" max="2040" value={form.delivery_year} onChange={(e) => set("delivery_year")(Number(e.target.value))} data-testid="pf-year" />
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("proj_form.f_address")}</label>
          <Input required value={form.address} onChange={set("address")} data-testid="pf-address" />
        </div>

        {/* Payment plan */}
        <div className="border-t border-border pt-6">
          <div className="mb-3">
            <div className="label-eyebrow">{t("proj_form.f_payment")}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("proj_form.f_payment_hint")}</p>
          </div>
          <div className="space-y-2" data-testid="pf-payment-list">
            {form.payment_plan.map((s, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Input
                  className="flex-1"
                  placeholder={t("proj_form.f_payment_stage")}
                  value={s.label}
                  onChange={(e) => patchPayment(i, "label", e.target.value)}
                  data-testid={`pf-payment-label-${i}`}
                />
                <div className="relative">
                  <Input
                    type="number"
                    min="0" max="100"
                    className="w-24 pr-7"
                    placeholder="0"
                    value={s.percent}
                    onChange={(e) => patchPayment(i, "percent", e.target.value)}
                    data-testid={`pf-payment-pct-${i}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removePayment(i)} data-testid={`pf-payment-remove-${i}`}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
            <Button type="button" variant="outline" size="sm" onClick={addPayment} data-testid="pf-payment-add">
              <Plus className="h-4 w-4 mr-2" /> {t("proj_form.f_payment_add")}
            </Button>
            <div className={`text-sm ${totalPercent === 100 ? "text-primary" : "text-muted-foreground"}`}>
              Total: <span className="font-semibold">{totalPercent}%</span>
            </div>
          </div>
        </div>

        {/* Units */}
        <div className="border-t border-border pt-6">
          <div className="mb-3">
            <div className="label-eyebrow">{t("proj_form.f_units")}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("proj_form.f_units_hint")}</p>
          </div>
          <div className="space-y-3" data-testid="pf-units-list">
            {form.units.map((u, i) => (
              <div key={i} className="border border-border rounded-lg p-3">
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("proj_form.f_unit_bedrooms")}</label>
                    <Input type="number" min="0" value={u.bedrooms} onChange={(e) => patchUnit(i, "bedrooms", e.target.value)} data-testid={`pf-unit-bd-${i}`} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("proj_form.f_unit_area")}</label>
                    <Input type="number" min="0" value={u.area_min || ""} onChange={(e) => patchUnit(i, "area_min", e.target.value)} data-testid={`pf-unit-area-${i}`} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("proj_form.f_unit_price")}</label>
                    <Input type="number" min="0" value={u.price_from || ""} onChange={(e) => patchUnit(i, "price_from", e.target.value)} data-testid={`pf-unit-price-${i}`} />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeUnit(i)} className="w-full" data-testid={`pf-unit-remove-${i}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <Input
                  className="mt-2"
                  placeholder={t("proj_form.f_unit_desc")}
                  value={u.description || ""}
                  onChange={(e) => patchUnit(i, "description", e.target.value)}
                  data-testid={`pf-unit-desc-${i}`}
                />
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addUnit} className="mt-3" data-testid="pf-unit-add">
            <Plus className="h-4 w-4 mr-2" /> {t("proj_form.f_unit_add")}
          </Button>
        </div>

        {/* Photos */}
        <div className="border-t border-border pt-6">
          <PhotoUploader photos={form.photos} onChange={(next) => setForm((p) => ({ ...p, photos: next }))} />
        </div>

        {/* Brochure */}
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">{t("proj_form.f_brochure")}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("proj_form.f_brochure_hint")}</p>
            </div>
            <div className="flex items-center gap-2">
              {form.brochure_path && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setForm((p) => ({ ...p, brochure_path: null }))}
                  data-testid="pf-brochure-remove"
                >
                  <X className="h-4 w-4 mr-1" /> {t("proj_form.brochure_remove")}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => brochureInputRef.current?.click()}
                disabled={uploadingBrochure}
                data-testid="pf-brochure-upload"
              >
                {uploadingBrochure ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {t("proj_form.brochure_upload")}
              </Button>
              <input
                ref={brochureInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => uploadBrochure(e.target.files?.[0])}
                className="hidden"
                data-testid="pf-brochure-file"
              />
            </div>
          </div>
          {form.brochure_path && (
            <a
              href={brochureUrl(form.brochure_path)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline underline-offset-4"
              data-testid="pf-brochure-preview"
            >
              <FileText className="h-4 w-4" /> Ver PDF actual
            </a>
          )}
        </div>

        {/* Location picker */}
        <div className="border-t border-border pt-6">
          <div className="mb-3">
            <div className="label-eyebrow">{t("form.f_location")}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("form.f_location_hint")}</p>
          </div>
          <LocationPicker
            lat={form.latitude}
            lng={form.longitude}
            onChange={(lat, lng) => setForm((p) => ({ ...p, latitude: lat, longitude: lng }))}
            height={280}
          />
        </div>

        {/* Confotur */}
        <div className="border-t border-border pt-6">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="pf-confotur-label">
            <Checkbox
              checked={form.confotur}
              onCheckedChange={(v) => setForm({ ...form, confotur: v === true })}
              data-testid="pf-confotur"
            />
            <div>
              <div className="inline-flex items-center gap-2 font-medium">
                <ShieldCheck className="h-4 w-4 text-secondary" />
                {t("proj_form.f_confotur")}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t("proj_form.f_confotur_hint")}</p>
            </div>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} className="rounded-full btn-hover-lift" data-testid="pf-submit">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? t("proj_form.save_changes") : t("proj_form.publish")}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/dashboard?tab=projects")} data-testid="pf-cancel">
            {t("proj_form.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
